import moment from "moment";
import {AccessScansReportRow, DisplayType} from "./type";
import {ColDef} from "ag-grid-community";

const generateColDefs = (): ColDef<AccessScansReportRow>[] => [
  {
    headerName: 'Scan Time',
    valueGetter: p => p.data?.adjusted_scan_time,
    pinned: 'left',
    valueFormatter: p => {
      const date = p.value ? moment.utc(p.value) : null;
      return date ? date.format('ddd, MMM DD, YYYY h:mm:ss a') : '';
    }
  },
  {
    headerName: 'Asset ID',
    valueGetter: p => p.data?.uid,
    filter: "agTextColumnFilter",
  },
  {
    headerName: 'Location Name',
    valueGetter: p => p.data?.location_name,
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'Access Type',
    valueGetter: p => p.data?.access_token_type,
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'Scan Status',
    valueGetter: p => p.data?.scan_status,
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'Scan Message',
    valueGetter: p => p.data?.scan_status_message,
    filter: 'agTextColumnFilter',
  },
];

export const colDefs: ColDef<AccessScansReportRow>[] = generateColDefs();

// Helper function to calculate totals
const calculateTotals = (data: AccessScansReportRow[]): { total: number; success: number; failed: number; rate: number } => {
  const total = data.length;
  const success = data.filter(row => row.scan_status === 'success').length;
  const failed = data.filter(row => row.scan_status === 'fail').length;
  const rate = total > 0 ? (success / total) * 100 : 0;
  return { total, success, failed, rate };
};

// Format data based on display type
export const formatData = (data: AccessScansReportRow[], displayType: DisplayType): AccessScansReportRow[] => {
  if (!data || data.length === 0) return [];

  // Mode 1: All Scans - return as-is
  if (displayType === 'all_scans') {
    return data;
  }

  // Mode 2: By Location
  if (displayType === 'by_location') {
    const grouped: Record<string, AccessScansReportRow[]> = {};

    data.forEach(scan => {
      const key = scan.location_name || 'Unknown Location';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(scan);
    });

    const result: AccessScansReportRow[] = [];
    Object.entries(grouped)
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([location, scans]) => {
        const totals = calculateTotals(scans);
        result.push({
          id: `location-${location}`,
          location_name: location,
          total_count: totals.total,
          success_count: totals.success,
          failed_count: totals.failed,
          success_rate: totals.rate,
          isGroupRow: true,
        } as AccessScansReportRow);
      });

    // Add total row
    const grandTotals = calculateTotals(data);
    result.push({
      id: 'total-row',
      location_name: 'Total',
      total_count: grandTotals.total,
      success_count: grandTotals.success,
      failed_count: grandTotals.failed,
      success_rate: grandTotals.rate,
      isTotalRow: true,
    } as AccessScansReportRow);

    return result;
  }

  // Mode 3: By Token Type
  if (displayType === 'by_token_type') {
    const grouped: Record<string, AccessScansReportRow[]> = {};

    data.forEach(scan => {
      const key = scan.access_token_type || 'Unknown Type';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(scan);
    });

    const result: AccessScansReportRow[] = [];
    Object.entries(grouped)
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([tokenType, scans]) => {
        const totals = calculateTotals(scans);
        result.push({
          id: `token-${tokenType}`,
          access_token_type: tokenType,
          total_count: totals.total,
          success_count: totals.success,
          failed_count: totals.failed,
          success_rate: totals.rate,
          isGroupRow: true,
        } as AccessScansReportRow);
      });

    // Add total row
    const grandTotals = calculateTotals(data);
    result.push({
      id: 'total-row',
      access_token_type: 'Total',
      total_count: grandTotals.total,
      success_count: grandTotals.success,
      failed_count: grandTotals.failed,
      success_rate: grandTotals.rate,
      isTotalRow: true,
    } as AccessScansReportRow);

    return result;
  }

  // Mode 4: By Date
  if (displayType === 'by_date') {
    const grouped: Record<string, AccessScansReportRow[]> = {};

    data.forEach(scan => {
      const date = scan.adjusted_scan_time
        ? moment.utc(scan.adjusted_scan_time).format('YYYY-MM-DD')
        : 'Unknown Date';
      if (!grouped[date]) grouped[date] = [];
      grouped[date].push(scan);
    });

    const result: AccessScansReportRow[] = [];
    Object.entries(grouped)
      .sort(([a], [b]) => b.localeCompare(a)) // Sort DESC (most recent first)
      .forEach(([date, scans]) => {
        const totals = calculateTotals(scans);
        result.push({
          id: `date-${date}`,
          group_date: date,
          total_count: totals.total,
          success_count: totals.success,
          failed_count: totals.failed,
          success_rate: totals.rate,
          isGroupRow: true,
        } as AccessScansReportRow);
      });

    // Add total row
    const grandTotals = calculateTotals(data);
    result.push({
      id: 'total-row',
      group_date: 'Total',
      total_count: grandTotals.total,
      success_count: grandTotals.success,
      failed_count: grandTotals.failed,
      success_rate: grandTotals.rate,
      isTotalRow: true,
    } as AccessScansReportRow);

    return result;
  }

  // Mode 5: By Location → Token Type (Flat with dynamic columns)
  if (displayType === 'by_location_token_type') {
    // Get unique token types
    const tokenTypes = Array.from(new Set(data.map(scan => scan.access_token_type || 'Unknown Type'))).sort();

    const grouped: Record<string, AccessScansReportRow[]> = {};

    data.forEach(scan => {
      const key = scan.location_name || 'Unknown Location';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(scan);
    });

    const result: AccessScansReportRow[] = [];
    Object.entries(grouped)
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([location, scans]) => {
        const row: any = {
          id: `location-token-${location}`,
          location_name: location,
          isGroupRow: true,
        };

        let totalScans = 0;
        let totalSuccess = 0;
        let totalFailed = 0;

        // For each token type, calculate success and fail counts
        tokenTypes.forEach(tokenType => {
          const tokenScans = scans.filter(s => (s.access_token_type || 'Unknown Type') === tokenType);
          const success = tokenScans.filter(s => s.scan_status === 'success').length;
          const failed = tokenScans.filter(s => s.scan_status === 'fail').length;

          row[`${tokenType}_success`] = success;
          row[`${tokenType}_failed`] = failed;
          totalScans += tokenScans.length;
          totalSuccess += success;
          totalFailed += failed;
        });

        row.total_count = totalScans;
        row.success_count = totalSuccess;
        row.failed_count = totalFailed;
        row.success_rate = totalScans > 0 ? (totalSuccess / totalScans) * 100 : 0;
        result.push(row as AccessScansReportRow);
      });

    // Add total row
    const totalRow: any = {
      id: 'total-row',
      location_name: 'Total',
      isTotalRow: true,
    };

    let grandTotal = 0;
    let grandSuccess = 0;
    let grandFailed = 0;
    tokenTypes.forEach(tokenType => {
      const tokenScans = data.filter(s => (s.access_token_type || 'Unknown Type') === tokenType);
      const success = tokenScans.filter(s => s.scan_status === 'success').length;
      const failed = tokenScans.filter(s => s.scan_status === 'fail').length;

      totalRow[`${tokenType}_success`] = success;
      totalRow[`${tokenType}_failed`] = failed;
      grandTotal += tokenScans.length;
      grandSuccess += success;
      grandFailed += failed;
    });

    totalRow.total_count = grandTotal;
    totalRow.success_count = grandSuccess;
    totalRow.failed_count = grandFailed;
    totalRow.success_rate = grandTotal > 0 ? (grandSuccess / grandTotal) * 100 : 0;
    result.push(totalRow as AccessScansReportRow);

    return result;
  }

  // Mode 6: By Date → Location (Flat with dynamic columns)
  if (displayType === 'by_date_location') {
    // Get unique locations
    const locations = Array.from(new Set(data.map(scan => scan.location_name || 'Unknown Location'))).sort();

    const grouped: Record<string, AccessScansReportRow[]> = {};

    data.forEach(scan => {
      const date = scan.adjusted_scan_time
        ? moment.utc(scan.adjusted_scan_time).format('YYYY-MM-DD')
        : 'Unknown Date';
      if (!grouped[date]) grouped[date] = [];
      grouped[date].push(scan);
    });

    const result: AccessScansReportRow[] = [];
    Object.entries(grouped)
      .sort(([a], [b]) => b.localeCompare(a)) // Sort DESC (most recent first)
      .forEach(([date, scans]) => {
        const row: any = {
          id: `date-location-${date}`,
          group_date: date,
          isGroupRow: true,
        };

        let totalScans = 0;
        let totalSuccess = 0;
        let totalFailed = 0;

        // For each location, calculate success and fail counts
        locations.forEach(location => {
          const locationScans = scans.filter(s => (s.location_name || 'Unknown Location') === location);
          const success = locationScans.filter(s => s.scan_status === 'success').length;
          const failed = locationScans.filter(s => s.scan_status === 'fail').length;

          row[`${location}_success`] = success;
          row[`${location}_failed`] = failed;
          totalScans += locationScans.length;
          totalSuccess += success;
          totalFailed += failed;
        });

        row.total_count = totalScans;
        row.success_count = totalSuccess;
        row.failed_count = totalFailed;
        row.success_rate = totalScans > 0 ? (totalSuccess / totalScans) * 100 : 0;
        result.push(row as AccessScansReportRow);
      });

    // Add total row
    const totalRow: any = {
      id: 'total-row',
      group_date: 'Total',
      isTotalRow: true,
    };

    let grandTotal = 0;
    let grandSuccess = 0;
    let grandFailed = 0;
    locations.forEach(location => {
      const locationScans = data.filter(s => (s.location_name || 'Unknown Location') === location);
      const success = locationScans.filter(s => s.scan_status === 'success').length;
      const failed = locationScans.filter(s => s.scan_status === 'fail').length;

      totalRow[`${location}_success`] = success;
      totalRow[`${location}_failed`] = failed;
      grandTotal += locationScans.length;
      grandSuccess += success;
      grandFailed += failed;
    });

    totalRow.total_count = grandTotal;
    totalRow.success_count = grandSuccess;
    totalRow.failed_count = grandFailed;
    totalRow.success_rate = grandTotal > 0 ? (grandSuccess / grandTotal) * 100 : 0;
    result.push(totalRow as AccessScansReportRow);

    return result;
  }

  return data;
};

// Get unique token types from data
export const getUniqueTokenTypes = (data: AccessScansReportRow[]): string[] => {
  if (!data || data.length === 0) return [];
  return Array.from(new Set(data.map(scan => scan.access_token_type || 'Unknown Type'))).sort();
};

// Get unique locations from data
export const getUniqueLocations = (data: AccessScansReportRow[]): string[] => {
  if (!data || data.length === 0) return [];
  return Array.from(new Set(data.map(scan => scan.location_name || 'Unknown Location'))).sort();
};

// Generate column definitions based on display type
export const generateGroupedColDefs = (
  displayType: DisplayType,
  tokenTypes: string[] = [],
  locations: string[] = []
): ColDef<AccessScansReportRow>[] => {
  const cols: ColDef<AccessScansReportRow>[] = [];

  const totalRowStyle = (params: any) => {
    if (params.data?.isTotalRow) {
      return { fontWeight: 'bold', backgroundColor: '#f3f4f6' };
    }
    return undefined;
  };

  // Mode 1: All Scans - use default columns
  if (displayType === 'all_scans') {
    return colDefs;
  }

  // Mode 2: By Location
  if (displayType === 'by_location') {
    cols.push(
      {
        headerName: 'Location',
        field: 'location_name',
        pinned: 'left',
        cellStyle: totalRowStyle,
        flex: 2,
        minWidth: 200,
      },
      {
        headerName: 'Total Scans',
        field: 'total_count',
        type: 'numericColumn',
        cellStyle: totalRowStyle,
        flex: 1,
        minWidth: 120,
      },
      {
        headerName: 'Successful',
        field: 'success_count',
        type: 'numericColumn',
        cellStyle: (params) => {
          const baseStyle = totalRowStyle(params);
          return { ...baseStyle, color: '#059669' };
        },
        flex: 1,
        minWidth: 120,
      },
      {
        headerName: 'Failed',
        field: 'failed_count',
        type: 'numericColumn',
        cellStyle: (params) => {
          const baseStyle = totalRowStyle(params);
          return { ...baseStyle, color: '#dc2626' };
        },
        flex: 1,
        minWidth: 100,
      },
      {
        headerName: 'Success Rate',
        field: 'success_rate',
        type: 'numericColumn',
        valueFormatter: (params) => params.value != null ? `${params.value.toFixed(1)}%` : '',
        cellStyle: totalRowStyle,
        flex: 1,
        minWidth: 130,
      }
    );
    return cols;
  }

  // Mode 3: By Token Type
  if (displayType === 'by_token_type') {
    cols.push(
      {
        headerName: 'Access Type',
        field: 'access_token_type',
        pinned: 'left',
        cellStyle: totalRowStyle,
        flex: 2,
        minWidth: 200,
      },
      {
        headerName: 'Total Scans',
        field: 'total_count',
        type: 'numericColumn',
        cellStyle: totalRowStyle,
        flex: 1,
        minWidth: 120,
      },
      {
        headerName: 'Successful',
        field: 'success_count',
        type: 'numericColumn',
        cellStyle: (params) => {
          const baseStyle = totalRowStyle(params);
          return { ...baseStyle, color: '#059669' };
        },
        flex: 1,
        minWidth: 120,
      },
      {
        headerName: 'Failed',
        field: 'failed_count',
        type: 'numericColumn',
        cellStyle: (params) => {
          const baseStyle = totalRowStyle(params);
          return { ...baseStyle, color: '#dc2626' };
        },
        flex: 1,
        minWidth: 100,
      },
      {
        headerName: 'Success Rate',
        field: 'success_rate',
        type: 'numericColumn',
        valueFormatter: (params) => params.value != null ? `${params.value.toFixed(1)}%` : '',
        cellStyle: totalRowStyle,
        flex: 1,
        minWidth: 130,
      }
    );
    return cols;
  }

  // Mode 4: By Date
  if (displayType === 'by_date') {
    cols.push(
      {
        headerName: 'Date',
        field: 'group_date',
        pinned: 'left',
        valueFormatter: (params) => {
          if (params.value === 'Total') return 'Total';
          return params.value ? moment(params.value).format('ddd, MMM DD, YYYY') : '';
        },
        cellStyle: totalRowStyle,
        flex: 2,
        minWidth: 200,
      },
      {
        headerName: 'Total Scans',
        field: 'total_count',
        type: 'numericColumn',
        cellStyle: totalRowStyle,
        flex: 1,
        minWidth: 120,
      },
      {
        headerName: 'Successful',
        field: 'success_count',
        type: 'numericColumn',
        cellStyle: (params) => {
          const baseStyle = totalRowStyle(params);
          return { ...baseStyle, color: '#059669' };
        },
        flex: 1,
        minWidth: 120,
      },
      {
        headerName: 'Failed',
        field: 'failed_count',
        type: 'numericColumn',
        cellStyle: (params) => {
          const baseStyle = totalRowStyle(params);
          return { ...baseStyle, color: '#dc2626' };
        },
        flex: 1,
        minWidth: 100,
      },
      {
        headerName: 'Success Rate',
        field: 'success_rate',
        type: 'numericColumn',
        valueFormatter: (params) => params.value != null ? `${params.value.toFixed(1)}%` : '',
        cellStyle: totalRowStyle,
        flex: 1,
        minWidth: 130,
      }
    );
    return cols;
  }

  // Mode 5: By Location → Token Type (dynamic columns)
  if (displayType === 'by_location_token_type') {
    cols.push({
      headerName: 'Location',
      field: 'location_name',
      pinned: 'left',
      cellStyle: totalRowStyle,
      flex: 2,
      minWidth: 200,
      wrapHeaderText: true,
      autoHeaderHeight: true,
    });

    // Add columns for each token type
    tokenTypes.forEach(tokenType => {
      cols.push(
        {
          headerName: `${tokenType} (Success)`,
          field: `${tokenType}_success`,
          type: 'numericColumn',
          cellStyle: (params) => {
            const baseStyle = totalRowStyle(params);
            return { ...baseStyle, color: '#059669' };
          },
          width: 150,
          minWidth: 150,
          wrapHeaderText: true,
          autoHeaderHeight: true,
        },
        {
          headerName: `${tokenType} (Failed)`,
          field: `${tokenType}_failed`,
          type: 'numericColumn',
          cellStyle: (params) => {
            const baseStyle = totalRowStyle(params);
            return { ...baseStyle, color: '#dc2626' };
          },
          width: 150,
          minWidth: 150,
          wrapHeaderText: true,
          autoHeaderHeight: true,
        }
      );
    });

    cols.push({
      headerName: 'Total Scans',
      field: 'total_count',
      type: 'numericColumn',
      cellStyle: totalRowStyle,
      width: 130,
      minWidth: 130,
      wrapHeaderText: true,
      autoHeaderHeight: true,
    });

    return cols;
  }

  // Mode 6: By Date → Location (dynamic columns)
  if (displayType === 'by_date_location') {
    cols.push({
      headerName: 'Date',
      field: 'group_date',
      pinned: 'left',
      valueFormatter: (params) => {
        if (params.value === 'Total') return 'Total';
        return params.value ? moment(params.value).format('ddd, MMM DD, YYYY') : '';
      },
      cellStyle: totalRowStyle,
      flex: 2,
      minWidth: 200,
      wrapHeaderText: true,
      autoHeaderHeight: true,
    });

    // Add columns for each location
    locations.forEach(location => {
      cols.push(
        {
          headerName: `${location} (Success)`,
          field: `${location}_success`,
          type: 'numericColumn',
          cellStyle: (params) => {
            const baseStyle = totalRowStyle(params);
            return { ...baseStyle, color: '#059669' };
          },
          width: 150,
          minWidth: 150,
          wrapHeaderText: true,
          autoHeaderHeight: true,
        },
        {
          headerName: `${location} (Failed)`,
          field: `${location}_failed`,
          type: 'numericColumn',
          cellStyle: (params) => {
            const baseStyle = totalRowStyle(params);
            return { ...baseStyle, color: '#dc2626' };
          },
          width: 150,
          minWidth: 150,
          wrapHeaderText: true,
          autoHeaderHeight: true,
        }
      );
    });

    cols.push({
      headerName: 'Total Scans',
      field: 'total_count',
      type: 'numericColumn',
      cellStyle: totalRowStyle,
      width: 130,
      minWidth: 130,
      wrapHeaderText: true,
      autoHeaderHeight: true,
    });

    return cols;
  }

  return colDefs;
};

// Hourly Ingress/Egress Helper Functions
export interface HourlyData {
  date: string;
  displayDate: string;
  hourlyData: {
    hour: number;
    ingressCount: number;
    egressCount: number;
  }[];
}

const isEgressScan = (accessTokenType: string): boolean => {
  const tokenLower = (accessTokenType || '').toLowerCase();
  return tokenLower.includes('egress');
};

// Helper to determine if scan is a valid ingress scan
// Valid ingress = NOT egress AND NOT shuttle
const isIngressScan = (accessTokenType: string): boolean => {
  if (!accessTokenType) return false;
  const tokenLower = accessTokenType.toLowerCase();
  return !tokenLower.includes('egress') && !tokenLower.includes('shuttle');
};

const formatHour = (hour: number): string => {
  const period = hour < 12 || hour === 24 ? 'am' : 'pm';
  const formattedHour = (hour % 12) || 12;
  return `${formattedHour}:00${period}`;
};

export const formatHourlyIngressEgressData = (data: AccessScansReportRow[]): HourlyData[] => {
  if (!data || data.length === 0) return [];

  // Filter for successful scans only (case-insensitive)
  const successfulScans = data.filter(scan =>
    scan.scan_status && scan.scan_status.toLowerCase() === 'success'
  );

  // Group by date
  const dateGroups: Record<string, AccessScansReportRow[]> = {};

  successfulScans.forEach(scan => {
    const date = scan.adjusted_scan_time
      ? moment.utc(scan.adjusted_scan_time).format('YYYY-MM-DD')
      : 'Unknown Date';
    if (!dateGroups[date]) dateGroups[date] = [];
    dateGroups[date].push(scan);
  });

  // For each date, create hourly counts
  const hourlyDataByDate: HourlyData[] = Object.entries(dateGroups)
    .sort(([a], [b]) => b.localeCompare(a)) // Sort DESC - newest dates first
    .map(([date, scans]) => {
      // Initialize hourly data (0-23 hours)
      const hourlyData = Array.from({ length: 24 }, (_, hour) => ({
        hour,
        ingressCount: 0,
        egressCount: 0,
      }));

      // Count scans by hour (ingress = NOT egress AND NOT shuttle)
      scans.forEach(scan => {
        if (scan.adjusted_scan_time) {
          const hour = moment.utc(scan.adjusted_scan_time).hour();
          if (isEgressScan(scan.access_token_type)) {
            hourlyData[hour].egressCount++;
          } else if (isIngressScan(scan.access_token_type)) {
            hourlyData[hour].ingressCount++;
          }
          // Note: Shuttle scans are excluded (not counted as ingress or egress)
        }
      });

      return {
        date,
        displayDate: moment(date).format('ddd MMM DD, YYYY'),
        hourlyData,
      };
    });

  return hourlyDataByDate;
};

// Hourly Attendance Helper Functions
export interface HourlyAttendanceData {
  date: string;
  displayDate: string;
  hourlyData: {
    hour: number;
    cumulativeAttendance: number;
  }[];
}

export const formatHourlyAttendanceData = (data: AccessScansReportRow[]): HourlyAttendanceData[] => {
  if (!data || data.length === 0) return [];

  // Filter for successful scans only (case-insensitive)
  const successfulScans = data.filter(scan =>
    scan.scan_status && scan.scan_status.toLowerCase() === 'success'
  );

  // Group by date
  const dateGroups: Record<string, AccessScansReportRow[]> = {};

  successfulScans.forEach(scan => {
    const date = scan.adjusted_scan_time
      ? moment.utc(scan.adjusted_scan_time).format('YYYY-MM-DD')
      : 'Unknown Date';
    if (!dateGroups[date]) dateGroups[date] = [];
    dateGroups[date].push(scan);
  });

  // For each date, create cumulative attendance by hour
  const hourlyAttendanceByDate: HourlyAttendanceData[] = Object.entries(dateGroups)
    .sort(([a], [b]) => b.localeCompare(a)) // Sort DESC - newest dates first
    .map(([date, scans]) => {
      // Initialize hourly data (0-23 hours)
      const hourlyData = Array.from({ length: 24 }, (_, hour) => ({
        hour,
        cumulativeAttendance: 0,
      }));

      // Sort scans by time to process chronologically
      const sortedScans = [...scans].sort((a, b) => {
        const timeA = a.adjusted_scan_time ? new Date(a.adjusted_scan_time).getTime() : 0;
        const timeB = b.adjusted_scan_time ? new Date(b.adjusted_scan_time).getTime() : 0;
        return timeA - timeB;
      });

      let runningTotal = 0;

      // Process each scan and update cumulative attendance for each hour
      sortedScans.forEach(scan => {
        if (scan.adjusted_scan_time) {
          const hour = moment.utc(scan.adjusted_scan_time).hour();

          if (isEgressScan(scan.access_token_type)) {
            runningTotal = Math.max(0, runningTotal - 1); // Never go below 0
          } else if (isIngressScan(scan.access_token_type)) {
            runningTotal++;
          }
          // Note: Shuttle scans are excluded

          // Update this hour and all subsequent hours with the new cumulative value
          for (let h = hour; h < 24; h++) {
            hourlyData[h].cumulativeAttendance = runningTotal;
          }
        }
      });

      return {
        date,
        displayDate: moment(date).format('ddd MMM DD, YYYY'),
        hourlyData,
      };
    });

  return hourlyAttendanceByDate;
};

// Convert hourly data to table rows
export const formatHourlyDataForTable = (hourlyData: HourlyData[]): AccessScansReportRow[] => {
  if (!hourlyData || hourlyData.length === 0) return [];

  const tableRows: AccessScansReportRow[] = [];

  hourlyData.forEach((dayData) => {
    // Create an ingress row
    const ingressRow: any = {
      id: `${dayData.date}-ingress`,
      group_date: dayData.displayDate,
      access_token_type: 'Ingress',
      scan_status: '',
      scan_status_message: '',
      location_name: '',
      username: '',
      uid: '',
      access_transaction_id: '',
      organization_id: 0,
      event_id: 0,
      location_id: 0,
      user_id: 0,
      access_token_quantity: 0,
      scan_created_at: '',
      adjusted_scan_time: '',
      organization_name: '',
      event_name: '',
    };

    // Add hourly counts using hour_5 through hour_28 naming
    for (let i = 0; i < 24; i++) {
      const hour = 5 + i; // hour_5 through hour_28
      const dataHour = (5 + i) % 24; // Actual hour index in dayData.hourlyData
      ingressRow[`hour_${hour}`] = dayData.hourlyData[dataHour].ingressCount;
    }

    tableRows.push(ingressRow as AccessScansReportRow);

    // Create an egress row
    const egressRow: any = {
      id: `${dayData.date}-egress`,
      group_date: dayData.displayDate,
      access_token_type: 'Egress',
      scan_status: '',
      scan_status_message: '',
      location_name: '',
      username: '',
      uid: '',
      access_transaction_id: '',
      organization_id: 0,
      event_id: 0,
      location_id: 0,
      user_id: 0,
      access_token_quantity: 0,
      scan_created_at: '',
      adjusted_scan_time: '',
      organization_name: '',
      event_name: '',
    };

    // Add hourly counts using hour_5 through hour_28 naming
    for (let i = 0; i < 24; i++) {
      const hour = 5 + i; // hour_5 through hour_28
      const dataHour = (5 + i) % 24; // Actual hour index in dayData.hourlyData
      egressRow[`hour_${hour}`] = dayData.hourlyData[dataHour].egressCount;
    }

    tableRows.push(egressRow as AccessScansReportRow);
  });

  return tableRows;
};

// Convert hourly attendance data to table rows
export const formatHourlyAttendanceDataForTable = (attendanceData: HourlyAttendanceData[]): AccessScansReportRow[] => {
  if (!attendanceData || attendanceData.length === 0) return [];

  const tableRows: AccessScansReportRow[] = [];

  attendanceData.forEach((dayData) => {
    // Create one row per day showing cumulative attendance
    const attendanceRow: any = {
      id: `${dayData.date}-attendance`,
      group_date: dayData.displayDate,
      access_token_type: '',
      scan_status: '',
      scan_status_message: '',
      location_name: '',
      username: '',
      uid: '',
      access_transaction_id: '',
      organization_id: 0,
      event_id: 0,
      location_id: 0,
      user_id: 0,
      access_token_quantity: 0,
      scan_created_at: '',
      adjusted_scan_time: '',
      organization_name: '',
      event_name: '',
    };

    // Add hourly cumulative attendance using hour_5 through hour_28 naming
    for (let i = 0; i < 24; i++) {
      const hour = 5 + i; // hour_5 through hour_28
      const dataHour = (5 + i) % 24; // Actual hour index in dayData.hourlyData
      attendanceRow[`hour_${hour}`] = dayData.hourlyData[dataHour].cumulativeAttendance;
    }

    tableRows.push(attendanceRow as AccessScansReportRow);
  });

  return tableRows;
};

// Generate column definitions for hourly view
export const generateHourlyColDefs = (): ColDef<AccessScansReportRow>[] => {
  const cols: ColDef<AccessScansReportRow>[] = [
    {
      headerName: 'Date',
      field: 'group_date',
      pinned: 'left',
      width: 155,
    },
    {
      headerName: 'Type',
      field: 'access_token_type',
      pinned: 'left',
      width: 80,
    },
  ];

  // Add columns for each hour starting at 5am (24 hours total)
  // Use hour_5 through hour_28 naming (like financial-hourly-report)
  for (let i = 0; i < 24; i++) {
    const hour = 5 + i; // Goes from 5 to 28
    const displayHour = (5 + i) % 24;
    const nextHour = (displayHour + 1) % 24;

    cols.push({
      headerName: `${formatHour(displayHour)} - ${formatHour(nextHour)}`,
      field: `hour_${hour}`,
      type: 'numericColumn',
      width: 150, // Static width to ensure full time range is always visible
      valueFormatter: (params) => params.value != null ? params.value.toString() : '0',
    });
  }

  return cols;
};